"use client";

import { useState, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { styled } from "@mui/system";
import axios from "axios";
import { AppStateContext } from "../../App";
import baseURL from "../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import axiosInstance from "../../helpers/Axios";

const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 30vh;
  padding: 2rem;
  background-color: #f3f4f6;
  margin-top: 5rem;
`;

const FormContainer = styled(Box)`
  background-color: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 28rem;
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 0.75rem;
    margin: 1rem;
  }
`;

const ResultCard = styled(Box)`
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  background-color: #2563eb;
  &:hover {
    background-color: #1d4ed8;
  }
`;

const Testformstorypage = () => {
  const [audioZip, setAudioZip] = useState(null);
  const [excelSheet, setExcelSheet] = useState(null);
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [open, setOpen] = useState(false);
  const { access_token } = useContext(AppStateContext);

  const handleAudioZipChange = (event) => {
    setAudioZip(event.target.files[0]);
  };

  const handleExcelSheetChange = (event) => {
    setExcelSheet(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!excelSheet || !audioZip) {
      alert("Please upload both the Excel file and ZIP file.");
      return;
    }

    if (!access_token) {
      alert("Unauthorized! Please log in.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("excel_file", excelSheet);
      formData.append("audio_files", audioZip);
      formData.append("is_default", isDefault);

      const response = await axiosInstance.post(
        `${baseURL}/api/trail/reading-content-story/bulk-upload/`,
        formData
      );

      setResponseData(response.data);
    } catch (error) {
      console.error("Error uploading files:", error);
      setResponseData({
        status: "error",
        message: "",
        errors: [],
        data: {
          message:
            error.response?.data?.message ||
            error.message ||
            "Upload failed! Please try again.",
        },
      });
    } finally {
      setLoading(false);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setAudioZip(null);
    setExcelSheet(null);
    setResponseData(null);
  };

  const renderFailedResults = () => {
    if (!responseData?.data?.results) return null;

    const failedResults = responseData.data.results.filter(
      (result) =>
        result.status === "failed" ||
        result.status === "updated" ||
        result.status === "created" ||
        result.status === "missing"
    );
    if (failedResults.length === 0) return null;

    return (
      <div className="mt-8 bg-white p-5 rounded-lg shadow-sm border border-gray-100">
        <Typography
          variant="h6"
          className="flex items-center gap-2 text-gray-800 font-medium mb-4">
          Results Uploads{" "}
          <span className="text-sm bg-gray-50 text-black-600 px-2 py-1 rounded-full">
            {failedResults.length}
          </span>
        </Typography>

        <div className="max-h-72 overflow-y-auto pr-2 space-y-4">
          {failedResults.map((result, index) => {
            // Define color schemes based on status
            const colorScheme = {
              failed: {
                bg: "bg-red-50",
                border: "border-red-200",
                text: "text-red-700",
                badge: "bg-red-100 text-red-800",
                leftBorder: "border-red-400",
                icon: "text-red-500",
              },
              updated: {
                bg: "bg-blue-50",
                border: "border-blue-200",
                text: "text-blue-700",
                badge: "bg-blue-100 text-blue-800",
                leftBorder: "border-blue-400",
                icon: "text-blue-500",
              },
              created: {
                bg: "bg-green-50",
                border: "border-green-200",
                text: "text-green-700",
                badge: "bg-green-100 text-green-800",
                leftBorder: "border-green-400",
                icon: "text-green-500",
              },
              missing: {
                bg: "bg-amber-50",
                border: "border-amber-200",
                text: "text-amber-700",
                badge: "bg-amber-100 text-amber-800",
                leftBorder: "border-amber-400",
                icon: "text-amber-500",
              },
            };

            // Get colors for current status or default to failed colors
            const colors = colorScheme[result.status] || colorScheme.failed;

            return (
              <div
                key={index}
                className={`${colors.bg} p-4 rounded-lg mb-3 border ${colors.border} shadow-sm`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <ErrorIcon className={`h-5 w-5 ${colors.icon} mr-2`} />
                    <span className={`font-medium ${colors.text}`}>
                      <span className="text-gray-600 mr-1">audio title:</span>{" "}
                      {result.title}
                    </span>
                  </div>
                  {result.status && (
                    <span
                      className={`text-xs ${colors.badge} px-2 py-1 rounded-full font-medium`}>
                      <span className="text-gray-600">status:</span>{" "}
                      {result.status}
                    </span>
                  )}
                  {result.audio_status && (
                    <span
                      className={`text-xs ${colors.badge} px-2 py-1 rounded-full font-medium ml-2`}>
                      <span className="text-gray-600">audio status:</span>{" "}
                      {result.audio_status}
                    </span>
                  )}
                </div>
                <div
                  className={`bg-white bg-opacity-50 rounded p-3 mt-1 border-l-2 ${colors.leftBorder}`}>
                  <span className="text-gray-600 text-sm font-medium mr-1">
                    error:
                  </span>
                  <p className={`text-sm ${colors.text} inline`}>
                    {result.error || "No error details available"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <StyledBox>
      <FormContainer>
        <Typography
          variant="h4"
          className="text-center mb-6 font-semibold text-gray-800">
          Upload Bulk Story Audio
        </Typography>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Audio ZIP File
            </label>
            <TextField
              type="file"
              accept=".zip"
              onChange={handleAudioZipChange}
              fullWidth
              variant="outlined"
              placeholder="Choose ZIP file"
              InputProps={{
                startAdornment: (
                  <AudiotrackIcon className="w-5 h-5 text-gray-400 mr-2" />
                ),
              }}
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Excel Sheet
            </label>
            <TextField
              type="file"
              accept=".xlsx, .xls"
              onChange={handleExcelSheetChange}
              fullWidth
              variant="outlined"
              placeholder="Choose Excel file"
              InputProps={{
                startAdornment: (
                  <DescriptionIcon className="w-5 h-5 text-gray-400 mr-2" />
                ),
              }}
            />
          </div>

          {/* Toggle button for default setting */}
          <div className="py-2">
            <FormControlLabel
              control={
                <Switch
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" className="text-gray-700">
                  Set as default
                </Typography>
              }
            />
          </div>

          <StyledButton
            type="submit"
            variant="contained"
            fullWidth
            className="py-2.5"
            disabled={loading}>
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Upload Files"
            )}
          </StyledButton>
        </form>
      </FormContainer>

      <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <div className="relative">
          <div className="px-6 py-4 border-b">
            <div className="flex justify-between items-center">
              <Typography variant="h6" className="text-gray-800">
                Upload Results
              </Typography>
              <IconButton
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600">
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <DialogContent className="px-6 py-5">
            {responseData && (
              <div className="space-y-4">
                <div
                  className={`flex items-center space-x-3 ${
                    responseData.status === "success"
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-700"
                  } p-4 rounded-lg`}>
                  {responseData.status === "success" ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <ErrorIcon className="h-5 w-5 text-red-500" />
                  )}
                  <p className="font-medium">{responseData.data?.message}</p>
                </div>

                {responseData.status === "success" && (
                  <div className="grid grid-cols-2 gap-4">
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">
                        Total Processed
                      </Typography>
                      <Typography variant="h6" className="text-gray-900">
                        {responseData.data.total_processed}
                      </Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">
                        Failed
                      </Typography>
                      <Typography variant="h6" className="text-gray-900">
                        {responseData.data.failed}
                      </Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">
                        Created/Updated
                      </Typography>
                      <Typography variant="h6" className="text-gray-900">
                        {responseData.data.created_or_updated}
                      </Typography>
                    </ResultCard>
                    <ResultCard>
                      <Typography variant="subtitle2" className="text-gray-500">
                        Audio Processed
                      </Typography>
                      <Typography variant="h6" className="text-gray-900">
                        {responseData.data.audio_files_processed}
                      </Typography>
                    </ResultCard>
                  </div>
                )}

                {renderFailedResults()}
              </div>
            )}
          </DialogContent>

          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex justify-end">
              <StyledButton onClick={handleClose} variant="contained">
                Close
              </StyledButton>
            </div>
          </div>
        </div>
      </StyledDialog>
    </StyledBox>
  );
};

export default Testformstorypage;
